import { animated } from '@react-spring/web'
import {
  type FocusEvent,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'

import { isNumeric } from '@lib/helpers'
import { type SanityFrameBuilderStandardSize } from '@frame-builder/data/sanity/queries/types/standard-size'
import { FrameBuilderSettingsContext } from '@frame-builder/contexts/settings-context'
import { FrameBuilderSelectionContext } from '@frame-builder/contexts/selection-context'
import { FrameBuilderSelectedStateContext } from '@frame-builder/contexts/state-context'
import { FrameBuilderStringsContext } from '@frame-builder/contexts/strings-context'
import { clamp } from '@frame-builder/lib/helpers'
import { useBlinkingState } from '@frame-builder/lib/preview-animation'
import { type StepDefinitionProps } from '@frame-builder/lib/steps'
import { getArtworkSizeSelectionError } from '@frame-builder/lib/validation/error-messages'

import Link from '@components/link'
import Button from '@frame-builder/components/utility/button'
import PortableText from '@frame-builder/components/utility/portable-text'
import ConfigurationDefinition from './configuration-definition'

const ArtworkSizeDefinition = ({
  stepNumber,
  onClose,
  className,
}: StepDefinitionProps) => {
  const { selectedState, dispatchSelectedState } = useContext(
    FrameBuilderSelectedStateContext,
  )
  const { selectedStateSelectionMessages } = useContext(
    FrameBuilderSelectionContext,
  )
  const { frameBuilderConfiguration, frameBuilderType } = useContext(
    FrameBuilderSettingsContext,
  )
  const frameBuilderStrings = useContext(FrameBuilderStringsContext)

  const [width, setWidth] = useState<string>(`${selectedState.artwork.width}`)
  const [height, setHeight] = useState<string>(
    `${selectedState.artwork.height}`,
  )
  const [selectedStandardSize, setSelectedStandardSize] =
    useState<SanityFrameBuilderStandardSize>()

  const [opacity, setIsBlinking] = useBlinkingState()

  const artworkSizeSelectionError = useMemo(() => {
    return getArtworkSizeSelectionError(
      frameBuilderConfiguration,
      {
        ...selectedState,
        artwork: {
          ...selectedState.artwork,
          width: parseFloat(width),
          height: parseFloat(height),
        },
      },
      frameBuilderStrings,
    )
  }, [
    frameBuilderConfiguration,
    frameBuilderStrings,
    height,
    selectedState,
    width,
  ])

  const isValidSelection = useMemo(() => {
    return !artworkSizeSelectionError
  }, [artworkSizeSelectionError])

  const normalizeSize = useCallback(
    (size: string, defaultValue: number): string => {
      if (!isNumeric(size)) {
        return `${defaultValue}`
      }

      const min = frameBuilderConfiguration.limits.minimumSize
      const max = Math.max(
        frameBuilderConfiguration.limits.maximumWidth,
        frameBuilderConfiguration.limits.maximumHeight,
      )
      const normalizedSize = clamp(parseFloat(size), min, max)

      return `${normalizedSize}`
    },
    [frameBuilderConfiguration],
  )

  const handleWidthBlur = useCallback(
    (event: FocusEvent<HTMLInputElement, Element>) => {
      const normalizedWidth = normalizeSize(
        event.target.value,
        selectedState.artwork.width,
      )

      setWidth(normalizedWidth)

      if (isNumeric(normalizedWidth)) {
        dispatchSelectedState({
          type: 'artworkSize',
          width: parseFloat(normalizedWidth),
        })
      }
    },
    [dispatchSelectedState, normalizeSize, selectedState.artwork.width],
  )

  const handleHeightBlur = useCallback(
    (event: FocusEvent<HTMLInputElement, Element>) => {
      const normalizedHeight = normalizeSize(
        event.target.value,
        selectedState.artwork.height,
      )

      setHeight(normalizedHeight)

      if (isNumeric(normalizedHeight)) {
        dispatchSelectedState({
          type: 'artworkSize',
          height: parseFloat(normalizedHeight),
        })
      }
    },
    [dispatchSelectedState, normalizeSize, selectedState.artwork.height],
  )

  const handleOkClick = useCallback(() => {
    if (isValidSelection) {
      onClose()
    }

    if (!isValidSelection) {
      setIsBlinking(true)
    }
  }, [isValidSelection, onClose, setIsBlinking])

  // Automatically match standard size based on frame dimensions
  useEffect(() => {
    const standardSize = frameBuilderConfiguration.standardSizes.find(
      (standardSize) => {
        const standardSizeWidth = `${standardSize.width}`
        const standardSizeHeight = `${standardSize.height}`

        return (
          (width === standardSizeWidth && height === standardSizeHeight) ||
          (height === standardSizeWidth && width === standardSizeHeight)
        )
      },
    )

    setSelectedStandardSize(standardSize)
  }, [frameBuilderConfiguration, height, width])

  return (
    <ConfigurationDefinition
      stepNumber={stepNumber}
      title={frameBuilderStrings.artworkSizeStepTitle}
      footerContent={frameBuilderStrings.artworkSizeStepFooter}
      onClose={onClose}
      className={className}
    >
      <div className="flex flex-col px-5 gap-y-5">
        <animated.div
          className="flex flex-col gap-y-5"
          style={{
            opacity,
          }}
        >
          <div className="space-y-2.5">
            <div className="flex items-end gap-x-8 text-sm">
              <div className="flex-1 flex flex-col justify-center gap-y-3">
                {!!frameBuilderStrings.artworkSizeWidthLabel && (
                  <span>{frameBuilderStrings.artworkSizeWidthLabel}</span>
                )}

                <div className="relative w-full ml-0">
                  <input
                    type="number"
                    min={frameBuilderConfiguration.limits.minimumSize}
                    max={Math.max(
                      frameBuilderConfiguration.limits.maximumWidth,
                      frameBuilderConfiguration.limits.maximumHeight,
                    )}
                    step={0.1}
                    value={width}
                    onChange={(event) => setWidth(event.target.value)}
                    onBlur={handleWidthBlur}
                    required
                    className="w-full min-w-[110px] py-2.5 pl-3 pr-10 border border-input-border invalid:border-error transition-colors duration-100"
                  />
                  <div className="inline-flex items-center justify-center absolute top-[1px] right-[1px] bottom-[1px] w-[42px] text-center text-gray-700 z-10">
                    {frameBuilderStrings.lengthUnitLabel}
                  </div>
                </div>
              </div>

              <div className="flex-1 flex flex-col justify-center gap-y-3">
                {!!frameBuilderStrings.artworkSizeHeightLabel && (
                  <span>{frameBuilderStrings.artworkSizeHeightLabel}</span>
                )}

                <div className="relative w-full">
                  <input
                    type="number"
                    min={frameBuilderConfiguration.limits.minimumSize}
                    max={Math.max(
                      frameBuilderConfiguration.limits.maximumWidth,
                      frameBuilderConfiguration.limits.maximumHeight,
                    )}
                    step={0.1}
                    value={height}
                    onChange={(event) => setHeight(event.target.value)}
                    onBlur={handleHeightBlur}
                    required
                    className="w-full min-w-[110px] py-2.5 pl-3 pr-10 border border-input-border invalid:border-error transition-colors duration-100"
                  />
                  <div className="inline-flex items-center justify-center absolute top-[1px] right-[1px] bottom-[1px] w-[42px] text-center text-gray-700 z-10">
                    {frameBuilderStrings.lengthUnitLabel}
                  </div>
                </div>
              </div>
            </div>

            {!!selectedStandardSize?.link && frameBuilderType === 'frame' && (
              <div className="px-3 py-2.5 rounded bg-warning text-sm">
                <span>
                  {frameBuilderStrings.artworkSizeStandardSizeMessage}{' '}
                </span>
                <Link link={selectedStandardSize.link}>
                  {frameBuilderStrings.artworkSizeStandardSizeLinkText}
                </Link>
              </div>
            )}

            {!!artworkSizeSelectionError && (
              <div className="px-3 py-2.5 rounded bg-error/10 text-sm">
                <span>{artworkSizeSelectionError}</span>
              </div>
            )}
          </div>

          {!!frameBuilderStrings.artworkSizeWidthAndHeightDescription && (
            <PortableText
              content={frameBuilderStrings.artworkSizeWidthAndHeightDescription}
            />
          )}
        </animated.div>

        <div className="border-t pt-2.5 space-y-5">
          {!!selectedStateSelectionMessages?.artworkSize && (
            <p className="text-base uppercase text-gray-700">
              {selectedStateSelectionMessages.artworkSize}
            </p>
          )}

          <div>
            <Button
              size="small"
              isVisuallyDisabled={!isValidSelection}
              onClick={handleOkClick}
              className="min-w-32 grow-0 shrink-0"
            >
              {frameBuilderStrings.controlOkLabel}
            </Button>
          </div>
        </div>
      </div>
    </ConfigurationDefinition>
  )
}

export default ArtworkSizeDefinition
